import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  FileText,
  Grid3X3,
  Layers3,
  Mail,
  MessageSquare,
  Save,
  Shield,
  ShieldCheck,
  Tag,
  Users,
} from 'lucide-react';
import TwoFactor from '../components/TwoFactor';
import OtpRoutingReadiness from '../components/OtpRoutingReadiness';
import SecurityDashboard from '../components/SecurityDashboard';
import { Skeleton } from '../ui/kit';
import GoLiveReset from '../components/GoLiveReset';
import ActualDistanceSettlement from '../components/ActualDistanceSettlement';
import StatusPanel from './StatusPanel';
import BonusQualificationPanel from './BonusQualificationPanel';
import MailAccountsPanel from './MailAccountsPanel';
import './Settings.css';

const inp = { width: '100%', padding: '9px 12px', background: '#e5e7eb', border: '1.5px solid #d1d5db', borderRadius: 9, color: '#0a0a0a', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const smallInp = { ...inp, padding: '7px 10px', fontSize: 12 };
const btn = (color = '#0a0a0a', extra = {}) => ({ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: color, color: '#fff', borderRadius: 9, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', ...extra });
const cardStyle = { background: '#ffffff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 24, marginBottom: 20 };
const FEE_AND_PHONE_KEYS = [
  'stop_fee',
  'wait_free_min',
  'wait_rate',
  'no_show_fee',
  'no_show_after_sec',
  'reservation_fee',
  'dispatch_hold_sec',
  'phone_masking_active',
  'voice_mask_number',
];

function SectionHead({ icon: Icon, color, title, sub }) {
  return (
    <div className="settings-section-head" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
      <div className="settings-section-icon" style={{ width: 40, height: 40, borderRadius: 10, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a', margin: 0 }}>{title}</h2>
        <p style={{ fontSize: 12, color: '#4b5563', margin: 0 }}>{sub}</p>
      </div>
    </div>
  );
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState('pricing');

  // Pricing config
  const [config, setConfig] = useState({});
  const [configLoading, setConfigLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  // Rechnungsdaten (GELD-UND-BELEGE-001): Aussteller-Identität als Owner-Feld.
  const [invoiceIdent, setInvoiceIdent] = useState(null);
  const [invoiceDraft, setInvoiceDraft] = useState({ name: '', address: '', vatId: '' });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const loadInvoiceIdentity = () => api.get('/admin/invoice-identity').then(r => {
    setInvoiceIdent(r.data);
    setInvoiceDraft({
      name: r.data.fields?.name?.config_value || '',
      address: r.data.fields?.address?.config_value || '',
      vatId: r.data.fields?.vatId?.config_value || '',
    });
  }).catch(() => {});

  useEffect(() => {
    api.get('/admin/config').then(r => { setConfig(r.data.config); setConfigLoading(false); }).catch(() => setConfigLoading(false));
    loadInvoiceIdentity();
  }, []);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const updates = Object.fromEntries(FEE_AND_PHONE_KEYS
        .filter(key => Object.prototype.hasOwnProperty.call(config, key))
        .map(key => [key, config[key]]));
      await api.patch('/admin/config', { updates });
      toast.success('Gespeichert');
    } catch (error) {
      const payload = error?.response?.data;
      const message = payload?.message || payload?.error || error?.message || 'Fehler';
      const keys = Array.isArray(payload?.keys) ? payload.keys.join(', ') : '';
      toast.error(keys ? `${message} Betroffene Schlüssel: ${keys}` : message);
    } finally { setSavingConfig(false); }
  };

  const saveInvoiceIdentity = async () => {
    setSavingInvoice(true);
    try {
      await api.post('/admin/invoice-identity', invoiceDraft);
      toast.success('Rechnungsdaten gespeichert — wirkt nur für neue Belege');
      loadInvoiceIdentity();
    } catch (error) {
      const status = error?.response?.status;
      toast.error(status === 403
        ? 'Nur der geschützte Owner darf Rechnungsdaten ändern'
        : (error?.response?.data?.message || 'Fehler beim Speichern'));
    } finally { setSavingInvoice(false); }
  };

  const tabs = [
    { k: 'pricing', label: 'Gebühren & Telefonie', info: 'Zusatzgebühren & Maskierung', icon: Tag },
    { k: 'invoice', label: 'Rechnungsdaten', info: 'Aussteller auf Belegen', icon: FileText },
    { k: 'driver', label: 'Fahrer-Status', info: 'Verfügbarkeit & Regeln', icon: Users },
    { k: 'mail', label: 'Mail-Konten', info: 'SMTP & Versand', icon: Mail },
    { k: 'otp-routing', label: 'WhatsApp & SMS', info: 'Bestätigungscodes & SMS-Ersatz', icon: MessageSquare },
    { k: 'security', label: 'Cyber Security', info: '2FA, Schutz & Reset', icon: Shield },
  ];
  const activeTabMeta = tabs.find(tab => tab.k === activeTab) || tabs[0];
  const ActiveTabIcon = activeTabMeta.icon;

  return (
    <div className="settings-root">
      <PageHeader
        title="Einstellungen"
        description="Gebühren, Betriebsabläufe, Kommunikation und Schutz zentral steuern. Die Preisautorität liegt vollständig in der Ultimate Engine."
        actions={
          <div className="settings-heading-badges" aria-label="Einstellungs-Kontext">
            <span className="is-brand"><ShieldCheck size={13} /> VOOGO Admin</span>
            <span><Layers3 size={13} /> {tabs.length} Bereiche</span>
          </div>
        }
      />

      <section className="settings-boundary" aria-label="Geltungsbereich">
        <span className="settings-boundary-icon"><AlertTriangle size={16} /></span>
        <div>
          <strong>Systemweite Konfiguration</strong>
          <p>Diese Einstellungen steuern weder den Fahrtarif noch Surge-/Push-Dynamik oder Provision. Neue Fahrten und Routenänderungen beziehen diese Werte ausschließlich aus der Ultimate Engine; alte Fahrten behalten ihre gespeicherten Werte.</p>
        </div>
        <span className="settings-boundary-state"><span /> Live-Konfiguration</span>
      </section>

      <section className="settings-metrics" aria-label="Einstellungsübersicht">
        <div>
          <span className="settings-metric-icon"><Grid3X3 size={16} /></span>
          <span><small>Arbeitsfläche</small><strong>{tabs.length} Bereiche</strong><em>Ein Control Center</em></span>
        </div>
        <div>
          <span className="settings-metric-icon"><Tag size={16} /></span>
          <span><small>Fahrzeugklassen</small><strong>6 Klassen</strong><em>Preis aus Engine</em></span>
        </div>
        <div>
          <span className="settings-metric-icon is-live"><Layers3 size={16} /></span>
          <span><small>Preisautorität</small><strong>Ultimate Engine</strong><em>Tarif · Dynamik · Provision</em></span>
        </div>
        <div>
          <span className="settings-metric-icon is-secure"><Shield size={16} /></span>
          <span><small>Geltungsbereich</small><strong>Systemweit</strong><em>VOOGO Admin</em></span>
        </div>
      </section>

      {/* Bereichsnavigation */}
      <nav className="settings-tab-grid" aria-label="Einstellungsbereiche">
        {tabs.map(({ k, label, info, icon: Icon }, index) => (
          <button key={k} onClick={() => setActiveTab(k)}
            type="button"
            aria-pressed={activeTab === k}
            className={activeTab === k ? 'is-active' : ''}>
            <span className="settings-tab-index">{String(index + 1).padStart(2, '0')}</span>
            <span className="settings-tab-icon"><Icon size={16} /></span>
            <span className="settings-tab-copy">
              <strong>{label}</strong>
              <small>{info}</small>
            </span>
          </button>
        ))}
      </nav>

      <div className="settings-section-marker">
        <span className="settings-section-marker-icon"><ActiveTabIcon size={16} /></span>
        <span><small>Aktiver Arbeitsbereich</small><strong>{activeTabMeta.label}</strong><em>{activeTabMeta.info}</em></span>
        <span className="settings-section-marker-count">{String(tabs.findIndex(tab => tab.k === activeTab) + 1).padStart(2, '0')} / {String(tabs.length).padStart(2, '0')}</span>
      </div>

      <div className="settings-active-panel">

      {activeTab === 'otp-routing' && <OtpRoutingReadiness />}

      {activeTab === 'security' && (
        <div className="settings-security-stack" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <SecurityDashboard />
          <TwoFactor />
          <GoLiveReset />
          <ActualDistanceSettlement />
          <div className="settings-content-card settings-sms-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 24, maxWidth: 520 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Shield size={20} color="#4b5563" />
              <h2 style={{ fontSize: 16, fontWeight: 800, color: '#0a0a0a' }}>SMS-2FA · Fahrer / Kunde / Partner</h2>
            </div>
            <p style={{ color: '#4b5563', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
              Einmal-Code beim Login (per WhatsApp oder SMS). <b>Fertig vorbereitet</b>, aber AUS — wird zum <b>Go-Live</b> aktiviert,
              sobald ein SMS-Anbieter (z. B. Twilio) hinterlegt ist. Ohne Anbieter bleibt der Schalter wirkungslos.
            </p>
            <button
              onClick={() => {
                const v = config.sms_2fa_active === '1' ? '0' : '1';
                setConfig(p => ({ ...p, sms_2fa_active: v }));
                api.patch('/admin/config', { updates: { sms_2fa_active: v } })
                  .then(() => toast.success(v === '1' ? 'SMS-2FA eingeschaltet (wirkt nur mit Anbieter)' : 'SMS-2FA ausgeschaltet'))
                  .catch(() => { setConfig(p => ({ ...p, sms_2fa_active: v === '1' ? '0' : '1' })); toast.error('Konnte nicht speichern'); });
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
              <span style={{ width: 46, height: 26, borderRadius: 999, background: config.sms_2fa_active === '1' ? '#9C27B0' : '#d1d5db', position: 'relative', transition: 'background .15s' }}>
                <span style={{ position: 'absolute', top: 3, left: config.sms_2fa_active === '1' ? 23 : 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0a0a0a' }}>{config.sms_2fa_active === '1' ? 'Eingeschaltet' : 'Ausgeschaltet'}</span>
            </button>
          </div>
        </div>
      )}

      {activeTab === 'pricing' && (
        <div className="settings-contract-note" style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#854d0e', lineHeight: 1.6 }}>
          <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> <strong>Die Ultimate Engine ist die einzige Quelle für Fahrtarif, Surge/Push und Provision.</strong> Diese Fläche speichert ausschließlich Zusatzgebühren und Telefonie; bestehende Fahrten bleiben unverändert.
        </div>
      )}

      {/* ── GEBÜHREN & TELEFONIE ── */}
      {activeTab === 'pricing' && (
        <div className="settings-content-card" style={cardStyle}>
          <SectionHead icon={Tag} color="#0a0a0a" title="Zusatzgebühren und Telefonie" sub="Gebühren und Rufnummern-Maskierung verwalten" />
          {configLoading ? <Skeleton h={120} r={12} /> : (
            <>
              <div className="settings-surface" style={{ background: '#e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 16, border: '1px solid #d1d5db' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563', marginBottom: 12 }}>Zusatzgebühren</div>
                <div className="settings-field-grid settings-field-grid--three" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  {[
                    { key: 'stop_fee', label: 'Zwischenstopp (€)' },
                    { key: 'wait_free_min', label: 'Warten kostenlos (Min)' },
                    { key: 'wait_rate', label: 'Wartegebühr (€/Min) · ohne Deckel' },
                    { key: 'no_show_fee', label: 'No-Show Gebühr (€)' },
                    { key: 'no_show_after_sec', label: 'No-Show nach (Sek., 420 = 7 Min)' },
                    { key: 'reservation_fee', label: 'Reservierungsgebühr (€)' },
                    { key: 'dispatch_hold_sec', label: 'Betriebssitz-Durchlauf (Sek., 0 = aus)', int: true, min: 0, max: 60, step: 1 },
                  ].map(({ key, label, int, min, max, step }) => (
                    <div key={key}>
                      <label style={{ fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>{label}</label>
                      {/* Ganzzahlige Sekundenfelder (Betriebssitz-Durchlauf) bekommen den Server-Vertrag 0..60, Schritt 1. */}
                      <input type="number" step={int ? (step || 1) : '0.01'} {...(int ? { min, max } : {})}
                        value={config[key] || ''} onChange={e => setConfig(p => ({ ...p, [key]: e.target.value }))} style={smallInp} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Rufnummern-Maskierung ㉗ (Twilio-Proxy): Backend liefert statt echter Nummern die Maskierungs-Nummer aus. */}
              <div className="settings-surface" style={{ background: '#e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 16, border: '1px solid #d1d5db' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#4b5563', marginBottom: 4 }}>Telefonie — Rufnummern-Maskierung</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                  Kunde &amp; Fahrer sehen statt der echten Nummer die Twilio-Nummer; der Anruf wird nur bei <b>aktiver Fahrt</b> zur Gegenseite verbunden.
                  Erst einschalten, wenn ein bezahltes Twilio-Konto mit Nummer existiert und deren Voice-Webhook auf <code>https://api.voogo.de/api/v1/voice/incoming</code> (HTTP POST) zeigt.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>Maskierung aktiv</label>
                    <select value={config.phone_masking_active || '0'} onChange={e => setConfig(p => ({ ...p, phone_masking_active: e.target.value }))} style={smallInp}>
                      <option value="0">Aus</option>
                      <option value="1">An</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>Twilio-Nummer (E.164, z. B. +4920312345678)</label>
                    <input type="text" placeholder="+49…" value={config.voice_mask_number || ''} onChange={e => setConfig(p => ({ ...p, voice_mask_number: e.target.value }))} style={smallInp} />
                  </div>
                </div>
              </div>

              <button onClick={saveConfig} disabled={savingConfig} style={{ ...btn('#0a0a0a'), opacity: savingConfig ? 0.6 : 1 }}>
                <Save size={15} /> {savingConfig ? 'Speichert...' : 'Gebühren & Telefonie speichern'}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── RECHNUNGSDATEN (GELD-UND-BELEGE-001) ── */}
      {activeTab === 'invoice' && (
        <div className="settings-content-card" style={cardStyle}>
          <SectionHead icon={FileText} color="#0a0a0a" title="Rechnungsdaten (Aussteller)"
            sub="Firmenname, Rechnungsadresse und USt-IdNr. auf allen VOOGO-Belegen" />
          {!invoiceIdent ? <Skeleton h={120} r={12} /> : (
            <>
              {invoiceIdent.missing?.length > 0 && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#991b1b', lineHeight: 1.5 }}>
                  <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                  Noch nicht gepflegt: {invoiceIdent.missing.join(', ')} — der Finanzblocker bleibt aktiv, bis die Felder hier eingetragen sind.
                </div>
              )}
              <div className="settings-surface" style={{ background: '#e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 14, border: '1px solid #d1d5db' }}>
                {[
                  { field: 'name', label: 'Firmenname' },
                  { field: 'address', label: 'Rechnungsadresse' },
                  { field: 'vatId', label: 'USt-IdNr.' },
                ].map(({ field, label }) => {
                  const meta = invoiceIdent.fields?.[field] || {};
                  const sourceLabel = meta.source === 'config' ? 'aus den Einstellungen'
                    : meta.source === 'env' ? 'aus der Server-Umgebung (.env)'
                    : 'dokumentierte Stammdaten (Rückfall)';
                  return (
                    <div key={field} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 5 }}>{label}</label>
                      <input type="text" maxLength={meta.max_length || 240}
                        value={invoiceDraft[field]}
                        disabled={!invoiceIdent.can_write}
                        title={!invoiceIdent.can_write ? 'Nur der geschützte Owner darf Rechnungsdaten ändern' : undefined}
                        placeholder={meta.value || ''}
                        onChange={e => setInvoiceDraft(p => ({ ...p, [field]: e.target.value }))} style={smallInp} />
                      <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4 }}>
                        Aktuell auf Belegen: <b>{meta.value || '–'}</b> · {sourceLabel}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                Änderungen wirken <b>nur für neue Belege</b> — bestehende Belege sind eingefrorene
                Snapshots und bleiben unverändert. Feld leeren + Speichern = zurück auf die
                Server-Umgebung bzw. die dokumentierten Stammdaten.
              </div>
              <button onClick={saveInvoiceIdentity} disabled={savingInvoice || !invoiceIdent.can_write}
                title={!invoiceIdent.can_write ? 'Nur der geschützte Owner darf Rechnungsdaten ändern' : undefined}
                style={{ ...btn('#0a0a0a'), opacity: (savingInvoice || !invoiceIdent.can_write) ? 0.6 : 1 }}>
                <Save size={15} /> {savingInvoice ? 'Speichert...' : 'Rechnungsdaten speichern'}
              </button>
            </>
          )}
        </div>
      )}

      {activeTab === 'driver' && (
        <>
          <StatusPanel />
          {/* BONUS-QUALIFIKATION-001: globale Qualifikation der Partner-Wochenboni */}
          <BonusQualificationPanel />
        </>
      )}

      {/* ── MAIL-KONTEN ── */}
      {activeTab === 'mail' && <MailAccountsPanel />}

      </div>
    </div>
  );
}
